import { ModuleConfirmModal } from "@client/content/controls/Modals/Confirm";
import { getRootContainerProps, getRootContainerPropsDep } from "@client/content/lib/helper";
import { ModuleButton } from "@client/content/modules/Button";
import { type CourseDetail } from "@client/content/modules/CourseDetail/types";
import { type ModuleApiInfo } from "@client/content/selectors";
import { type RootContainerProps } from "@client/content/types";
import LessonAdditionalInfo from "@common/components/Courses/Lesson/AdditionalInfo/component";
import { LessonBody } from "@common/components/Courses/Lesson/component";
import { type ButtonProps } from "@common/controls/Button/component";
import classNames from "classnames";
import * as React from "react";
import { useResizeDetector } from "react-resize-detector";

export type MapProps = RootContainerProps & {
  allFaq: CourseDetail.LessonFaq[];
  apiInfo: ModuleApiInfo;
  courseId: string;
  data: CourseDetail.CourseLessonAppState | null;
  lessonId: string | null;
  previousLessonId: string | null;
  nextLessonId: string | null;
};

export interface ActionProps {
  enter: (courseId: string, lessonId: string) => void;
  leave: (courseId: string, lessonId: string) => void;
  onArrowClick: (lessonId: string) => void;
  quizBack: (lessonId: string) => void;
  quizReviewEnd: (lessonId: string) => void;
  quizStart: (lessonId: string) => void;
  quizReview: (lessonId: string) => void;
  quizProceed: (lessonId: string, questionId: string, answerIds: string[]) => void;
}

const LessonComponent = (props: MapProps & ActionProps) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const footerRef = React.useRef<HTMLDivElement>(null);
  const lessonBbodyRef = React.useRef<HTMLDivElement>(null);

  const components = React.useMemo(
    () => (buttonProps: ButtonProps) => ({
      ButtonComponent: <ModuleButton {...getRootContainerProps(props)} {...buttonProps} />,
      ConfirmModalComponent: (
        <ModuleConfirmModal {...getRootContainerProps(props)} {...buttonProps} onConfirm={() => {}} isVisible={true} />
      )
    }),
    [...getRootContainerPropsDep(props)]
  );

  const updateFooterHeight = React.useCallback(() => {
    if (!lessonBbodyRef.current || !footerRef.current || !containerRef.current) return;

    if (lessonBbodyRef.current.clientHeight > containerRef.current.offsetHeight - footerRef.current.offsetHeight) {
      footerRef.current.style.maxHeight = `75px`;
      footerRef.current.style.position = "sticky";
    } else {
      footerRef.current.style.maxHeight = "unset";
      footerRef.current.style.position = "unset";
    }
  }, []);

  const onAdditionalInfoTabActivated = React.useCallback(() => {
    setTimeout(() => {
      if (!lessonBbodyRef.current || !containerRef.current) return;

      containerRef.current.scrollTo({
        top: lessonBbodyRef.current.offsetHeight + 1,
        behavior: "smooth"
      });
    }, 10);
  }, []);

  const { ref: lessonBodyContainerRef } = useResizeDetector<HTMLDivElement>({
    handleHeight: true,
    onResize: updateFooterHeight
  });

  if (!props.data) return null;

  return (
    <div
      ref={containerRef}
      className={classNames("lesson", props.data.lesson.programModuleLessonTypeKey.toLowerCase())}
    >
      <LessonBody
        bodyContainerRef={lessonBodyContainerRef as any}
        bodyRef={lessonBbodyRef}
        {...props}
        {...components}
      />

      <hr />
      <div ref={footerRef} className="footer">
        <LessonAdditionalInfo data={props.data} allFaq={props.allFaq} onTabActivated={onAdditionalInfoTabActivated} />
      </div>
    </div>
  );
};

export default LessonComponent;
